import React from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  InputBase,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { Logout, Person } from "@mui/icons-material";
import { useThemeMode } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import FilterSettingsDialog from "./FilterSettingsDialog";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { useTarget } from "../contexts/TargetContext";

const AppBarModern: React.FC = () => {
  const { toggleMode } = useThemeMode();
  const { user, logout, isAuthenticated } = useAuth();
  const { search, setSearch } = useTarget();
  const navigate = useNavigate();
  const [userMenuAnchor, setUserMenuAnchor] =
    React.useState<null | HTMLElement>(null);
  const [filterSettingsOpen, setFilterSettingsOpen] = React.useState(false);

  const handleUserMenuClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isAuthenticated) {
      setUserMenuAnchor(e.currentTarget);
    } else {
      navigate("/signin");
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      setUserMenuAnchor(null);
      navigate("/signin");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleProfile = () => {
    setUserMenuAnchor(null);
    navigate("/profile");
  };

  return (
    <AppBar
      position="fixed"
      color="default"
      elevation={2}
      sx={{ zIndex: 1300 }}
    >
      <Toolbar>
        <Typography
          variant="h6"
          noWrap
          sx={{
            flexGrow: 0,
            fontWeight: 700,
            cursor: "pointer",
          }}
          onClick={() => navigate("/")}
        >
          AstroTracker
        </Typography>
        <Box sx={{ flexGrow: 1 }} />

        {/* Only show search box when authenticated */}
        {isAuthenticated && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              bgcolor: "background.paper",
              borderRadius: 2,
              px: 2,
              mr: 2,
              height: 40,
            }}
          >
            <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
            <InputBase
              placeholder="Search targets/frames…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ color: "text.primary", width: 200 }}
            />
            {search && (
              <IconButton
                size="small"
                aria-label="Clear search"
                onClick={() => setSearch("")}
                edge="end"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )}

        {/* Theme toggle is always visible */}
        <IconButton color="inherit" sx={{ mr: 1 }} onClick={toggleMode}>
          <Brightness4Icon />
        </IconButton>

        {/* Only show settings when authenticated */}
        {isAuthenticated && (
          <IconButton
            color="inherit"
            sx={{ mr: 1 }}
            onClick={() => setFilterSettingsOpen(true)}
          >
            <SettingsIcon />
          </IconButton>
        )}

        {/* User menu button */}
        <IconButton
          color="inherit"
          onClick={handleUserMenuClick}
          sx={{
            p: isAuthenticated && user?.avatarUrl ? 0.5 : 1,
          }}
        >
          {isAuthenticated && user?.avatarUrl ? (
            <Avatar
              src={user.avatarUrl}
              alt={user.name}
              sx={{ width: 32, height: 32 }}
            />
          ) : (
            <AccountCircle />
          )}
        </IconButton>

        {/* User menu */}
        <Menu
          anchorEl={userMenuAnchor}
          open={!!userMenuAnchor}
          onClose={() => setUserMenuAnchor(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          {isAuthenticated && (
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle1" noWrap>
                {user?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {user?.email}
              </Typography>
            </Box>
          )}
          {isAuthenticated && <Divider />}
          <MenuItem onClick={handleProfile}>
            <ListItemIcon>
              <Person fontSize="small" />
            </ListItemIcon>
            Profile
          </MenuItem>
          {isAuthenticated ? (
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          ) : (
            <MenuItem onClick={() => navigate("/signin")}>
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              Sign In
            </MenuItem>
          )}
        </Menu>

        {/* Filter settings dialog */}
        <FilterSettingsDialog
          open={filterSettingsOpen}
          onClose={() => setFilterSettingsOpen(false)}
        />
      </Toolbar>
    </AppBar>
  );
};

export default AppBarModern;
